/**
 * TealerLab — Quick Buy modal
 * ────────────────────────────
 * Modal global qui s'ouvre depuis n'importe quel bouton [data-tlr-quick-buy].
 * Flow : click → fetch /products/{handle}.js → render → POST /cart/add.js → cart drawer.
 *
 * Compatible avec n'importe quel gateway de paiement Shopify (Stripe, NMI,
 * passerelle CBD-friendly, OVRY-via-Shopify-checkout). Pas de dépendance Shop Pay.
 */
(function () {
  // Wording comes from the snippet's data-label-* attributes ({{ … | t }});
  // the literals below are only a fallback when the markup predates them.
  const LABELS = {
    idle: 'AJOUTER AU POCHON',
    busy: 'AJOUT EN COURS...',
    error: 'ERREUR — RÉESSAYER',
    save: 'Tu économises {amount}',
    more: 'Voir plus',
    less: 'Voir moins',
    from: 'À partir de',
    slide: 'Image {index}',
    perUnit: '/unité',
  };

  let modal, elMedia,
    elTitle, elSub, elMore, elImg, elDots, elChips, elVariants,
    elPrice, elCompare, elSaving, elPerGram, elCta;
  let currentProduct = null;
  let currentVariant = null;
  // Gallery (same engine as the product page — see the Médias section below).
  let sliderCore = null;   // Promise of assets/tlr-slider.js + its Swiper element
  let sliderApi = null;    // that module once resolved: mount() / unmount()
  let stageSwiper = null;  // live Swiper instance, null while the stage is plain
  let mountInitialSlide = 0; // native-scroller position handed to Swiper at mount
  let mediaToken = 0;      // invalidates a mount left in flight by a product switch
  let productToken = 0;    // invalidates a product fetch overtaken by a newer open()
  let dotsFrame = false;   // rAF throttle for the native-scroll dot sync
  let hideTimer = null;    // hide() delay, cleared when show() wins the race

  function init() {
    modal = document.querySelector('[data-tlr-qb]');
    if (!modal) return;
    LABELS.idle = modal.getAttribute('data-label-idle') || LABELS.idle;
    LABELS.busy = modal.getAttribute('data-label-busy') || LABELS.busy;
    LABELS.error = modal.getAttribute('data-label-error') || LABELS.error;
    LABELS.save = modal.getAttribute('data-label-save') || LABELS.save;
    LABELS.more = modal.getAttribute('data-label-more') || LABELS.more;
    LABELS.less = modal.getAttribute('data-label-less') || LABELS.less;
    LABELS.from = modal.getAttribute('data-label-from') || LABELS.from;
    LABELS.slide = modal.getAttribute('data-label-slide') || LABELS.slide;
    LABELS.perUnit = modal.getAttribute('data-label-per-unit') || LABELS.perUnit;
    elMedia = modal.querySelector('[data-tlr-qb-media]');
    elTitle = modal.querySelector('[data-tlr-qb-title]');
    elSub = modal.querySelector('[data-tlr-qb-sub]');
    elMore = modal.querySelector('[data-tlr-qb-more]');
    elImg = modal.querySelector('[data-tlr-qb-img]');
    elDots = modal.querySelector('[data-tlr-qb-dots]');
    elChips = modal.querySelector('[data-tlr-qb-chips]');
    elVariants = modal.querySelector('[data-tlr-qb-variants]');
    elPrice = modal.querySelector('[data-tlr-qb-price]');
    elCompare = modal.querySelector('[data-tlr-qb-compare]');
    elSaving = modal.querySelector('[data-tlr-qb-saving]');
    elPerGram = modal.querySelector('[data-tlr-qb-per-gram]');
    elCta = modal.querySelector('[data-tlr-qb-confirm]');
    modal.setAttribute('data-tlr-entry', 'quick-buy');

    document.addEventListener('click', delegateOpen);
    modal.addEventListener('click', handleModalClick);
    // Mounting swaps the stage element for a <swiper-container> (and unmounting
    // swaps it back), so the gallery events are heard on the modal, which never
    // moves — the same contract the product page uses for its own dots.
    modal.addEventListener('tlr:slider:init', (e) => {
      if (!isStage(e.target) || !e.detail?.swiper) return;
      const sw = e.detail.swiper;
      stageSwiper = sw;
      sw.on('slideChange', () => setActiveDot(sw.realIndex || 0));
      // Hand the native scroller position over: whatever the shopper reached
      // (dot click or swipe) before the engine landed, the mounted gallery
      // starts there instead of snapping back to slide 1. Dispatched before
      // paint, so the handoff is invisible.
      if (mountInitialSlide > 0) sw.slideTo(mountInitialSlide, 0);
      mountInitialSlide = 0;
      setActiveDot(sw.realIndex || 0);
    });
    modal.addEventListener('tlr:slider:destroy', (e) => {
      if (isStage(e.target)) stageSwiper = null;
    });
    // While the stage is still the native scroller (engine downloading, or
    // unavailable), swipes must keep driving the dots. Scroll does not bubble:
    // capture on the modal survives the stage being swapped at mount.
    modal.addEventListener('scroll', (e) => {
      if (!isStage(e.target) || stageSwiper || dotsFrame) return;
      dotsFrame = true;
      requestAnimationFrame(() => {
        dotsFrame = false;
        const st = e.target;
        setActiveDot(Math.round(st.scrollLeft / Math.max(1, st.clientWidth)));
      });
    }, { capture: true, passive: true });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.hasAttribute('data-open')) hide();
    });
  }

  function delegateOpen(e) {
    const btn = e.target.closest('[data-tlr-quick-buy]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    // ── INSTANT PREVIEW ──
    // Avant de fetch /products/X.js (qui prend 100-500ms), on récupère
    // ce qui est déjà rendu sur la card source (image, chips, rating, bg).
    // Comme tout est déjà en cache navigateur, ça s'affiche INSTANTANÉMENT.
    // Le fetch met ensuite à jour le titre, variants, prix.
    const card = btn.closest('.tlr-pcard, [data-product-card], .card');
    setExposedProduct(card ? productIdOf(card) : '');
    if (card) {
      const cardImg = card.querySelector('.tlr-pcard__media img, img');
      if (cardImg && (cardImg.currentSrc || cardImg.src)) {
        elImg.src = cardImg.currentSrc || cardImg.src;
        elImg.alt = cardImg.alt || '';
      }
      // Clone des chips (PACK PREROLL / HYBRID / CBX) directement depuis le DOM
      if (elChips) {
        const cardChips = card.querySelector('.tlr-pcard__chips');
        if (cardChips) {
          elChips.innerHTML = cardChips.innerHTML;
        } else {
          elChips.innerHTML = '';
        }
      }
      // Clone du rating (étoiles + N avis) depuis la card
      const elRating = modal.querySelector('[data-tlr-qb-rating]');
      if (elRating) {
        const cardRating = card.querySelector('.tlr-pcard__rating');
        if (cardRating) {
          elRating.innerHTML = cardRating.innerHTML;
        } else {
          elRating.innerHTML = '';
        }
      }
    }
    // Couleur de fond produit : lue directement depuis data-bg du bouton
    // (metafield tealerlab.bg_color) → fond modal = couleur dominante PDP
    if (elMedia) {
      const bgColor = btn.getAttribute('data-bg');
      if (bgColor && bgColor.trim()) {
        elMedia.style.setProperty('--tlr-qb-bg', bgColor.trim());
      } else {
        elMedia.style.removeProperty('--tlr-qb-bg');
      }
    }
    // Reset état texte → skeleton léger pendant fetch
    elTitle.textContent = '…';
    elSub.textContent = '';
    collapseDescription();
    if (elMore) elMore.hidden = true;
    elPrice.textContent = '…';
    elCompare.hidden = true;
    elSaving.hidden = true;
    if (elPerGram) elPerGram.hidden = true;
    elChips.innerHTML = '';
    elVariants.innerHTML = '';
    resetMedia();
    elCta.disabled = true;

    // Ouvre le modal IMMÉDIATEMENT (l'image preview est déjà visible)
    show();

    // Fetch les détails complets en parallèle
    open(btn.getAttribute('data-handle'), parseInt(btn.getAttribute('data-default-variant'), 10));
  }

  async function open(handle, defaultVariantId) {
    if (!handle) return;
    // Out-of-order guard: without it, a slow answer for product A lands AFTER
    // product B was opened and silently swaps the whole modal (title, price,
    // variant) — a tap on ADD then buys the wrong product. Proven in review.
    const token = ++productToken;
    let product;
    try {
      const r = await fetch(`/products/${handle}.js`, { cache: 'no-store' });
      if (!r.ok) return;
      product = await r.json();
    } catch (_) { return; }
    if (token !== productToken) return;
    currentProduct = product;
    setExposedProduct(product.id);

    currentVariant =
      currentProduct.variants.find((v) => v.id === defaultVariantId && v.available) ||
      currentProduct.variants.find((v) => v.available) ||
      currentProduct.variants[0];

    renderAll();
  }

  function renderAll() {
    elTitle.textContent = currentProduct.title;
    // Description entière : le clamp CSS coupe l'affichage, le bouton la déplie.
    elSub.textContent = currentProduct.description ? stripHtml(currentProduct.description) : '';
    renderDescriptionToggle();

    // Ne remplace l'image QUE si le preview de card pointe pas déjà sur le bon fichier
    // → évite un flash quand l'image preview est déjà la bonne (cache navigateur)
    const newImg = currentProduct.featured_image || '';
    if (newImg) {
      const curBase = baseName(elImg.src || '');
      const newBase = baseName(newImg);
      if (curBase !== newBase) {
        elImg.src = sized(newImg, 600);
      }
      elImg.alt = currentProduct.title;
    }
    renderMedia();

    // renderChips() désactivé : on utilise le clone depuis la card source (DOM)
    // qui contient les chips calculées (PACK PREROLL / HYBRID / CBX) au lieu
    // des tags Shopify préfixés (type-, effet-...) qui n'existent pas toujours.
    // Si pas de card source détectée (cas exotique), fallback sur renderChips().
    if (!elChips || !elChips.innerHTML.trim()) {
      renderChips();
    }
    renderVariants();
    renderPrice();

    elCta.disabled = false;
    elCta.querySelector('span').textContent = LABELS.idle;
  }

  // ── Description ──
  // Le clamp vit en CSS ; le bouton n'apparaît que si le texte déborde vraiment,
  // ce qui ne se mesure qu'une fois la modale ouverte (elle l'est déjà ici).
  function collapseDescription() {
    elSub.classList.remove('is-expanded');
    if (!elMore) return;
    elMore.setAttribute('aria-expanded', 'false');
    elMore.textContent = LABELS.more;
  }

  function renderDescriptionToggle() {
    collapseDescription();
    if (!elMore) return;
    elMore.hidden = elSub.scrollHeight <= elSub.clientHeight + 1;
  }

  function toggleDescription() {
    const expanded = elSub.classList.toggle('is-expanded');
    elMore.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    elMore.textContent = expanded ? LABELS.less : LABELS.more;
  }

  // ── Médias ──
  // Le stage EST la galerie de la fiche produit : même socle slider
  // (assets/tlr-slider.js), mêmes slides, mêmes dots. Deux différences que la
  // PDP n'a pas : les slides n'arrivent qu'après le fetch produit, et la modale
  // sert TOUS les produits — d'où un montage explicite après injection, et un
  // démontage complet avant de servir le produit suivant.
  // La 1re slide est celle du preview de card : elle n'est jamais reconstruite,
  // c'est elle qui donne sa hauteur au pane.
  function stageEl() {
    return modal.querySelector('[data-tlr-qb-stage]');
  }

  function isStage(el) {
    return !!el && el.nodeType === 1 && el.hasAttribute('data-tlr-qb-stage');
  }

  // The engine is already on the page (the snippet renders its loader tag):
  // importing it by that same URL hands back that instance, so the record its
  // initial scan created — what makes mount() legal — is the one we drive.
  // Swiper itself is loaded here too: mount() promotes the stage and calls
  // initialize() at once, which needs <swiper-container> already defined.
  function loadGallery() {
    if (!sliderCore) {
      const src = modal.getAttribute('data-tlr-slider-src');
      if (!src) return null;
      sliderCore = Promise.all([import(src), import('@theme/tlr-swiper')])
        .then(([core]) => customElements.whenDefined('swiper-container').then(() => core));
    }
    return sliderCore;
  }

  function resetMedia() {
    mediaToken += 1;
    mountInitialSlide = 0;
    stageSwiper = null;
    let stage = stageEl();
    if (!stage) return;
    // Give the engine its plain container back BEFORE touching the slides:
    // removing them under a live Swiper would leave its transform and its slide
    // list pointing at the previous product.
    if (sliderApi && stage.tagName === 'SWIPER-CONTAINER') {
      sliderApi.unmount(stage);
      stage = stageEl();
    }
    // If the engine did not hand the plain container back, stripping slides
    // under a live Swiper is the one thing this function must never do.
    if (!stage || stage.tagName === 'SWIPER-CONTAINER') return;
    while (stage.children.length > 1) stage.lastElementChild.remove();
    stage.scrollLeft = 0;
    if (elDots) {
      elDots.innerHTML = '';
      elDots.hidden = true;
    }
  }

  function renderMedia() {
    if (!stageEl()) return;
    resetMedia();
    const featured = baseName(currentProduct.featured_image || '');
    const extras = (currentProduct.media || []).filter(
      (m) => m.media_type === 'image' && m.src && baseName(m.src) !== featured
    );
    const stage = stageEl();
    // Mirror of the resetMedia guard: if the engine kept its live container,
    // injecting raw divs into it is the one thing to never do.
    if (!stage || stage.tagName === 'SWIPER-CONTAINER') return;
    extras.forEach((m) => {
      const slide = document.createElement('div');
      slide.className = 'tlr-qb__slide';
      const img = document.createElement('img');
      img.src = sized(m.src, 600);
      img.srcset = `${sized(m.src, 300)} 300w, ${sized(m.src, 450)} 450w, ${sized(m.src, 600)} 600w`;
      img.sizes = '(min-width: 768px) 300px, 50vw';
      img.alt = m.alt || currentProduct.title;
      img.loading = 'lazy';
      img.decoding = 'async';
      img.width = m.width || 600;
      img.height = m.height || 600;
      slide.appendChild(img);
      stage.appendChild(slide);
    });
    renderDots(extras.length + 1);
    mountStage(extras.length + 1);
  }

  async function mountStage(slideCount) {
    // One media: the static slide IS the gallery. A Swiper there would only add
    // a shadow root, and the dots stay hidden.
    if (slideCount < 2) return;
    const token = mediaToken;
    try {
      const core = await loadGallery();
      // Another product (or a reset) took over while Swiper was downloading.
      if (!core || token !== mediaToken || modal.hidden) return;
      const stage = stageEl();
      if (!stage || stage.tagName === 'SWIPER-CONTAINER') return;
      sliderApi = core;
      mountInitialSlide = Math.round(stage.scrollLeft / Math.max(1, stage.clientWidth));
      core.mount(stage);
    } catch (_) {
      // Engine unavailable (offline, stale import map): the stage stays the
      // native scroll-snap scroller it was rendered as, medias still swipeable.
    }
  }

  function renderDots(count) {
    if (!elDots) return;
    elDots.innerHTML = '';
    if (count <= 1) { elDots.hidden = true; return; }
    for (let i = 0; i < count; i += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'tlr-qb__dot' + (i === 0 ? ' is-active' : '');
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      dot.setAttribute('aria-label', LABELS.slide.replace('{index}', () => String(i + 1)));
      dot.setAttribute('data-tlr-qb-dot', String(i));
      elDots.appendChild(dot);
    }
    elDots.hidden = false;
  }

  function setActiveDot(index) {
    if (!elDots) return;
    Array.from(elDots.children).forEach((dot, i) => {
      const on = i === index;
      dot.classList.toggle('is-active', on);
      dot.setAttribute('aria-selected', on ? 'true' : 'false');
    });
  }

  // Dots are drawn as soon as the product is known, Swiper lands a few hundred
  // ms later: a click in that window is replayed on mount, never dropped.
  function goToSlide(index) {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!stageSwiper || stageSwiper.destroyed) {
      // Engine not there yet (downloading — the whole first-open window on a
      // slow network — or unavailable): the dot drives the native scroller,
      // exactly what it did before the engine existed. scrollLeft is then the
      // single source of truth the mount handoff reads.
      const stage = stageEl();
      if (stage && stage.tagName !== 'SWIPER-CONTAINER') {
        stage.scrollTo({ left: index * stage.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
        setActiveDot(index);
      }
      return;
    }
    // undefined speed = the engine default; 0 for people who asked for less
    // motion, as the scroller this replaces already did.
    stageSwiper.slideTo(index, reduce ? 0 : undefined);
  }

  function renderChips() {
    elChips.innerHTML = '';
    const allowedPrefixes = ['type-', 'effet-', 'taux-', 'culture-', 'origine-'];
    const tags = (currentProduct.tags || []).slice(0, 6);
    tags.forEach((t) => {
      const matched = allowedPrefixes.find((p) => t.toLowerCase().startsWith(p));
      if (matched) {
        const label = t.substring(matched.length).toUpperCase();
        const chip = document.createElement('span');
        chip.className = 'tlr-qb__chip';
        chip.textContent = label;
        elChips.appendChild(chip);
      }
    });
  }

  function renderVariants() {
    if (!currentProduct.variants || currentProduct.variants.length <= 1) {
      elVariants.parentElement.hidden = true;
      return;
    }
    elVariants.parentElement.hidden = false;
    elVariants.innerHTML = currentProduct.variants
      .map((v) => `
        <button type="button"
          class="tlr-qb__pill ${v.id === currentVariant.id ? 'is-active' : ''} ${!v.available ? 'is-soldout' : ''}"
          data-variant-id="${v.id}"
          aria-pressed="${v.id === currentVariant.id ? 'true' : 'false'}"
          ${!v.available ? 'disabled' : ''}>${escapeHtml(v.title)}</button>
      `)
      .join('');
  }

  // Même lecture que la card (snippets/tlr-variant-measure.liquid) : le titre
  // porte soit un poids (« 50G + 50G Offerts » = 100 g reçus pour le prix, les
  // grammes offerts comptent), soit un nombre d'unités (« 20 Unités »), soit
  // rien — « 5mg » et « 5 gummies » ne sont ni l'un ni l'autre.
  function measureOf(variant) {
    const title = String(variant.title || '').toLowerCase().trim().replace(/,/g, '.');
    const parts = title.split('+');
    const weight = parts[0].trim().match(/^(\d+(?:\.\d+)?)\s*g(?:r|(?![a-zà-ÿ]))/);
    if (weight) {
      let tenths = Math.round(parseFloat(weight[1]) * 10);
      if (tenths > 0) {
        const paid = tenths;
        for (const part of parts.slice(1)) {
          // a weight ("g" then a word break), or a bare number repeating the paid weight
          // before a free wording ("3,5 free"); "1 Free Grinder" is an item, not a gram
          const m = part.trim().match(/^(\d+(?:\.\d+)?)\s*(g(?:r|(?![a-zà-ÿ]))|free|offert|gratuit)/);
          if (!m) continue;
          const add = Math.round(parseFloat(m[1]) * 10);
          if (m[2].startsWith('g') || add === paid) tenths += add;
        }
        return { qty: tenths / 10, kind: 'g' };
      }
    }
    const compact = title.replace(/\s+/g, '');
    const count = compact.match(/^(\d+)(unit[eé]s?)$/);
    if (count) return { qty: parseInt(count[1], 10), kind: 'u' };
    return { qty: 0, kind: 'none' };
  }

  function perMeasureCents(variant) {
    const { qty } = measureOf(variant);
    if (!qty) return 0;
    // Même arithmétique que la card : division entière tronquée, décimale arrondie.
    const cents = variant.price / qty;
    return Number.isInteger(qty) ? Math.floor(cents) : Math.round(cents);
  }

  function measureSuffix(variant) {
    return measureOf(variant).kind === 'u' ? LABELS.perUnit : '/gr';
  }

  function renderPerGram() {
    if (!elPerGram) return;
    const active = perMeasureCents(currentVariant);
    if (active > 0) {
      elPerGram.textContent = `${formatMoney(active / 100)}${measureSuffix(currentVariant)}`;
      elPerGram.hidden = false;
      return;
    }
    // Repli de la card : rien à afficher pour la variante active, on annonce la
    // meilleure mesure du produit.
    const others = (currentProduct.variants || []).filter((v) => perMeasureCents(v) > 0);
    if (!others.length) { elPerGram.hidden = true; return; }
    const best = others.reduce((a, b) => (perMeasureCents(a) <= perMeasureCents(b) ? a : b));
    elPerGram.textContent = `${LABELS.from} ${formatMoney(perMeasureCents(best) / 100)}${measureSuffix(best)}`;
    elPerGram.hidden = false;
  }

  function renderPrice() {
    elPrice.textContent = formatMoney(currentVariant.price / 100);

    const cmp = currentVariant.compare_at_price;
    if (cmp && cmp > currentVariant.price) {
      elCompare.textContent = formatMoney(cmp / 100);
      elCompare.hidden = false;
      // Remplaçant fonction : neutralise les motifs $& d'une traduction.
      const amount = formatMoney((cmp - currentVariant.price) / 100);
      elSaving.textContent = LABELS.save.replace('{amount}', () => amount);
      elSaving.hidden = false;
    } else {
      elCompare.hidden = true;
      elSaving.hidden = true;
    }
    renderPerGram();
  }

  function handleModalClick(e) {
    if (e.target.closest('[data-tlr-qb-close]')) { hide(); return; }

    const pill = e.target.closest('.tlr-qb__pill[data-variant-id]');
    if (pill && !pill.disabled) {
      const id = parseInt(pill.dataset.variantId, 10);
      const v = currentProduct.variants.find((x) => x.id === id);
      if (v) {
        const changed = !currentVariant || currentVariant.id !== v.id;
        currentVariant = v;
        renderVariants();
        renderPrice();
        if (changed) publish('tlr_variant', { product: String(currentProduct.id), variant: String(v.id), option: optionNames(currentProduct) });
      }
      return;
    }

    const dot = e.target.closest('[data-tlr-qb-dot]');
    if (dot) {
      goToSlide(parseInt(dot.getAttribute('data-tlr-qb-dot'), 10) || 0);
      return;
    }

    if (e.target.closest('[data-tlr-qb-more]')) { toggleDescription(); return; }

    if (e.target.closest('[data-tlr-qb-confirm]')) { confirm(); }
  }

  async function confirm() {
    if (!currentVariant || !currentVariant.available) return;
    // Captured before the await: another product may be open by the time the add answers.
    const variant = currentVariant;
    const product = currentProduct;
    elCta.disabled = true;
    elCta.querySelector('span').textContent = LABELS.busy;

    try {
      // A third-party app (free-gift) monkey-patches window.fetch and can hold
      // the request open forever while its own modal waits for a click — which
      // can freeze this CTA on the busy label with an empty cart.
      // A buy button NEVER stays stuck: race the add against a hard timeout and
      // hand the shopper back an actionable button.
      const r = await Promise.race([
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variant.id, quantity: 1, properties: { _tlr: 'v2', _tlr_entry: window.tlr?.events?.entryFor?.(variant.id, 'quick-buy') } }] }),
        }),
        new Promise((_, reject) => setTimeout(() => reject(new Error('add timeout')), 8000)),
      ]);
      if (!r.ok) throw new Error('add failed');
      window.tlr?.events?.added?.({ entry: 'quick-buy', variant: variant.id, qty: 1, product: product && product.id });
      // Same convention as the other TLR add-to-cart entry points: notify the theme,
      // close the modal, then hand over to the cart drawer (no jump to /checkout).
      document.dispatchEvent(new CustomEvent('cart:updated'));
      elCta.disabled = false;
      elCta.querySelector('span').textContent = LABELS.idle;
      hide();
      // Degrade to the cart page when the drawer script is absent, never a dead end.
      if (typeof window.tlrOpenCart === 'function') {
        window.tlrOpenCart();
      } else {
        window.location.href = '/cart';
      }
    } catch (_) {
      elCta.disabled = false;
      elCta.querySelector('span').textContent = LABELS.error;
    }
  }

  // Scroll lock partagé avec le cart drawer et le menu drawer (.tlr-scroll-locked,
  // assets/tlr-menu-drawer.css) : overflow:hidden sur html + body, supporté iOS 15.4+.
  // L'ancien lock position:fixed obligeait un window.scrollTo() à la fermeture, que
  // le `scroll-behavior: smooth` global (base.css) transformait en scroll animé
  // parasite — c'est le saut de page constaté après fermeture de l'achat express.
  function lockBodyScroll() {
    document.documentElement.classList.add('tlr-scroll-locked');
    document.body.classList.add('tlr-scroll-locked');
  }
  function unlockBodyScroll() {
    document.documentElement.classList.remove('tlr-scroll-locked');
    document.body.classList.remove('tlr-scroll-locked');
  }

  function show() {
    // A hide() fired < 300ms ago would blank the modal we are opening.
    clearTimeout(hideTimer);
    modal.hidden = false;
    requestAnimationFrame(() => modal.setAttribute('data-open', ''));
    lockBodyScroll();
  }
  function hide() {
    // Anything still in flight belongs to a modal that no longer exists: a
    // late product answer must not render, a late Swiper must not mount into
    // display:none (it would measure everything at 0).
    productToken += 1;
    mediaToken += 1;
    modal.removeAttribute('data-open');
    unlockBodyScroll();
    hideTimer = setTimeout(() => { modal.hidden = true; }, 300);
  }

  // Même formatage que les autres modules prix du thème (cart drawer, cards).
  function formatMoney(amount) {
    try {
      return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: window.tlr?.currency || 'EUR' }).format(amount);
    } catch (_) {
      return amount.toFixed(2).replace('.', ',') + ' €';
    }
  }
  function baseName(url) {
    return String(url || '').split('?')[0].split('/').pop();
  }
  // Les médias de /products/X.js arrivent en pleine résolution : on demande au CDN
  // la largeur réellement affichée.
  function sized(url, width) {
    if (!url) return url;
    return url + (url.includes('?') ? '&' : '?') + 'width=' + width;
  }
  function publish(type, data) {
    window.tlr?.events?.publish?.(type, data);
  }
  function optionNames(product) {
    return (product.options || []).map((o) => (typeof o === 'string' ? o : o && o.name) || '').filter(Boolean).join(' / ');
  }
  // Every card carries its product id (data-tlr-products).
  function productIdOf(card) {
    return card.getAttribute('data-tlr-products') || card.getAttribute('data-product-id') || '';
  }
  function setExposedProduct(id) {
    if (id) modal.setAttribute('data-tlr-products', String(id));
    else modal.removeAttribute('data-tlr-products');
  }
  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }
  function stripHtml(s) {
    const div = document.createElement('div');
    // Block tags produce no whitespace in textContent: the heading glued itself to
    // the next paragraph ("…Résine de CaractèreLa Black Ketama…"). Separate them.
    div.innerHTML = String(s == null ? '' : s).replace(/<\/?(p|div|h[1-6]|li|ul|ol|dl|dt|dd|table|tr|td|th|blockquote|section|article)\b[^>]*>|<(br|hr)\b[^>]*\/?>/gi, '$& ');
    return (div.textContent || '').replace(/\s+/g, ' ').trim();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
